import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, Trophy, Gift, RotateCcw, Tv, Copy, Check, 
  Sparkles, ArrowLeft, Plus, Trash2, Hash
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

// Predefined ticket colors matching common cloakroom ticket colors
const TICKET_COLORS = [
  { name: 'green', hex: '#22c55e', label: 'Green' },
  { name: 'blue', hex: '#3b82f6', label: 'Blue' },
  { name: 'pink', hex: '#ec4899', label: 'Pink' },
  { name: 'yellow', hex: '#eab308', label: 'Yellow' },
  { name: 'orange', hex: '#f97316', label: 'Orange' },
  { name: 'red', hex: '#ef4444', label: 'Red' },
  { name: 'purple', hex: '#a855f7', label: 'Purple' },
  { name: 'white', hex: '#f1f5f9', label: 'White' },
];

const getColorHex = (colorName) => {
  const color = TICKET_COLORS.find(c => c.name === colorName.toLowerCase());
  return color ? color.hex : '#6b7280';
};

export default function CloakroomRaffleHostPage() {
  const { sessionCode: urlSessionCode } = useParams();
  
  const [session, setSession] = useState(null);
  const [hostName, setHostName] = useState('');
  const [books, setBooks] = useState([
    { color: 'green', serial: '', start_number: 1, end_number: 1000 }
  ]);
  const [prizeLabel, setPrizeLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [spinningTicket, setSpinningTicket] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [lastWinner, setLastWinner] = useState(null);
  const spinIntervalRef = useRef(null);

  const displayUrl = session ? `${window.location.origin}/cloakroom/display/${session.session_code}` : '';

  const fetchSession = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/raffle/cloakroom/sessions/${code}`);
      if (res.ok) {
        const data = await res.json();
        setSession(data);
      } else if (res.status === 404) {
        // Session expired or server restarted - clear state
        setSession(null);
        toast.error('Session expired. Please create a new raffle.');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  useEffect(() => {
    if (urlSessionCode) {
      fetchSession(urlSessionCode);
    }
  }, [urlSessionCode]);

  // Periodically check if session is still valid (every 30 seconds)
  useEffect(() => {
    if (session?.session_code) {
      const interval = setInterval(() => {
        fetchSession(session.session_code);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [session?.session_code]);

  const addBook = () => {
    setBooks([...books, { color: 'blue', serial: '', start_number: 1, end_number: 1000 }]);
  };

  const removeBook = (index) => {
    if (books.length > 1) {
      setBooks(books.filter((_, i) => i !== index));
    }
  };

  const updateBook = (index, field, value) => {
    const updated = [...books];
    updated[index][field] = value;
    setBooks(updated);
  };

  const createSession = async () => {
    if (!hostName.trim()) {
      toast.error('Please enter a host name');
      return;
    }
    
    // Validate books
    for (let i = 0; i < books.length; i++) {
      if (!books[i].serial.trim()) {
        toast.error(`Please enter a serial number for book ${i + 1}`);
        return;
      }
      if (books[i].start_number >= books[i].end_number) {
        toast.error(`Invalid range for book ${i + 1}: start must be less than end`);
        return;
      }
    }

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/raffle/cloakroom/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host_name: hostName,
          books: books
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSession(data);
        toast.success(`Cloakroom raffle created! ${data.total_tickets} tickets in the draw.`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      toast.error('Failed to create cloakroom raffle');
    } finally {
      setCreating(false);
    }
  };

  const drawTicket = async () => {
    if (!session) return;
    
    setDrawing(true);
    setShowResult(false);
    setLastWinner(null);

    // Spin animation - show random tickets
    let spinCount = 0;
    const maxSpins = 20;
    const allTickets = [];
    session.books.forEach(book => {
      for (let i = book.start_number; i <= Math.min(book.start_number + 50, book.end_number); i++) {
        allTickets.push({ number: i, color: book.color, serial: book.serial });
      }
    });

    spinIntervalRef.current = setInterval(() => {
      const randomTicket = allTickets[Math.floor(Math.random() * allTickets.length)];
      setSpinningTicket(randomTicket);
      spinCount++;
      
      if (spinCount >= maxSpins) {
        clearInterval(spinIntervalRef.current);
        actualDraw();
      }
    }, 100);
  };

  const actualDraw = async () => {
    try {
      const res = await fetch(`${API_URL}/api/raffle/cloakroom/sessions/${session.session_code}/draw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prize_label: prizeLabel || undefined })
      });

      if (res.ok) {
        const data = await res.json();
        setLastWinner(data);
        setSpinningTicket(data.winning_ticket);
        setTimeout(() => {
          setShowResult(true);
          setDrawing(false);
          fetchSession(session.session_code);
        }, 500);
      } else {
        const error = await res.json();
        if (res.status === 404) {
          // Session expired or server restarted
          toast.error('Session expired. Please create a new raffle.');
          setSession(null);
        } else {
          toast.error(error.detail || 'Draw failed');
        }
        setDrawing(false);
        setSpinningTicket(null);
      }
    } catch (error) {
      toast.error('Failed to draw ticket - please try again');
      setDrawing(false);
      setSpinningTicket(null);
    }
  };

  const resetSession = async () => {
    if (!session) return;
    
    try {
      const res = await fetch(`${API_URL}/api/raffle/cloakroom/sessions/${session.session_code}/reset`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('All tickets back in the draw!');
        setLastWinner(null);
        setShowResult(false);
        setPrizeLabel('');
        fetchSession(session.session_code);
      }
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const copyDisplayUrl = () => {
    navigator.clipboard.writeText(displayUrl);
    setCopied(true);
    toast.success('Display URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Setup screen
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/bingo/host" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-7 h-7 text-amber-400" />
                Cloakroom Ticket Raffle
              </h1>
              <p className="text-slate-400 text-sm">Draw winners from physical ticket books</p>
            </div>
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Setup Your Raffle</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Host Name */}
              <div>
                <Label className="text-slate-300">Host Name</Label>
                <Input
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="e.g., Line Dance Central"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>

              {/* Ticket Books */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-slate-300">Ticket Books</Label>
                  <Button
                    onClick={addBook}
                    size="sm"
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Book
                  </Button>
                </div>

                <div className="space-y-4">
                  {books.map((book, index) => (
                    <div 
                      key={index} 
                      className="p-4 rounded-lg border-2"
                      style={{ 
                        backgroundColor: `${getColorHex(book.color)}20`,
                        borderColor: getColorHex(book.color)
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white font-medium">Book {index + 1}</span>
                        {books.length > 1 && (
                          <Button
                            onClick={() => removeBook(index)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-slate-400 text-xs">Colour</Label>
                          <Select
                            value={book.color}
                            onValueChange={(val) => updateBook(index, 'color', val)}
                          >
                            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TICKET_COLORS.map(color => (
                                <SelectItem key={color.name} value={color.name}>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-4 h-4 rounded"
                                      style={{ backgroundColor: color.hex }}
                                    />
                                    {color.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-slate-400 text-xs">Serial Number</Label>
                          <Input
                            value={book.serial}
                            onChange={(e) => updateBook(index, 'serial', e.target.value.toUpperCase())}
                            placeholder="e.g., AD 212772"
                            className="bg-slate-700 border-slate-600 text-white mt-1"
                          />
                        </div>

                        <div>
                          <Label className="text-slate-400 text-xs">Start Number</Label>
                          <Input
                            type="number"
                            value={book.start_number}
                            onChange={(e) => updateBook(index, 'start_number', parseInt(e.target.value) || 1)}
                            className="bg-slate-700 border-slate-600 text-white mt-1"
                          />
                        </div>

                        <div>
                          <Label className="text-slate-400 text-xs">End Number</Label>
                          <Input
                            type="number"
                            value={book.end_number}
                            onChange={(e) => updateBook(index, 'end_number', parseInt(e.target.value) || 1000)}
                            className="bg-slate-700 border-slate-600 text-white mt-1"
                          />
                        </div>
                      </div>

                      <p className="text-xs text-slate-400 mt-2">
                        {book.end_number - book.start_number + 1} tickets in this book
                      </p>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-slate-400 mt-3">
                  Total: {books.reduce((sum, b) => sum + (b.end_number - b.start_number + 1), 0)} tickets across {books.length} book{books.length > 1 ? 's' : ''}
                </p>
              </div>

              <Button
                onClick={createSession}
                disabled={creating}
                className="w-full bg-amber-600 hover:bg-amber-500 text-lg py-6"
              >
                {creating ? 'Creating...' : 'Create Raffle'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Active session - Host controls
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Ticket className="w-8 h-8 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold text-white">Cloakroom Raffle</h1>
              <p className="text-slate-400 text-sm">Session: {session.session_code}</p>
            </div>
          </div>
          <Badge variant="outline" className="border-amber-500 text-amber-400">
            {session.remaining_tickets} tickets remaining
          </Badge>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Draw Controls */}
          <div className="space-y-4">
            {/* Ticket Display */}
            <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
              <CardContent className="p-6">
                <AnimatePresence mode="wait">
                  {spinningTicket ? (
                    <motion.div
                      key={showResult ? 'result' : 'spinning'}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      className="relative"
                    >
                      {/* Ticket visualization */}
                      <div 
                        className="rounded-xl p-6 text-center relative overflow-hidden"
                        style={{ backgroundColor: getColorHex(spinningTicket.color) }}
                      >
                        <div className="bg-white rounded-lg p-4 shadow-lg">
                          <p className="text-slate-600 text-sm font-mono mb-1">
                            {spinningTicket.serial}
                          </p>
                          <p className={`font-bold ${showResult ? 'text-6xl' : 'text-5xl'}`}>
                            {spinningTicket.number}
                          </p>
                        </div>
                        {showResult && lastWinner && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-4"
                          >
                            <Badge className="bg-white/90 text-slate-900 text-lg px-4 py-1">
                              {lastWinner.prize_label}
                            </Badge>
                          </motion.div>
                        )}
                      </div>
                      {showResult && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-2 -right-2"
                        >
                          <Trophy className="w-12 h-12 text-amber-400 drop-shadow-lg" />
                        </motion.div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-8"
                    >
                      <Ticket className="w-16 h-16 mx-auto text-slate-600 mb-3" />
                      <p className="text-slate-400">Ready to draw</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Prize Label */}
            <div>
              <Label className="text-slate-300">Prize Label (optional)</Label>
              <Input
                value={prizeLabel}
                onChange={(e) => setPrizeLabel(e.target.value)}
                placeholder="e.g., 1st Prize, Door Prize"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>

            {/* Draw Button */}
            <Button
              onClick={drawTicket}
              disabled={drawing || session.remaining_tickets === 0}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-xl py-8"
            >
              {drawing ? (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-6 h-6 animate-pulse" />
                  Drawing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Gift className="w-6 h-6" />
                  Draw Winning Ticket
                </span>
              )}
            </Button>

            {/* Reset */}
            <Button
              onClick={resetSession}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset All Tickets
            </Button>
          </div>

          {/* Right: Info & Display Link */}
          <div className="space-y-4">
            {/* Books Summary */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Ticket Books</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {session.books.map((book, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 rounded"
                      style={{ backgroundColor: `${getColorHex(book.color)}30` }}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: getColorHex(book.color) }}
                        />
                        <span className="text-white font-mono text-sm">{book.serial}</span>
                      </div>
                      <span className="text-slate-400 text-sm">
                        {book.start_number}-{book.end_number}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Display URL */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Tv className="w-5 h-5 text-amber-400" />
                  TV Display
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-lg">
                    <QRCodeSVG value={displayUrl} size={120} />
                  </div>
                </div>
                <Button
                  onClick={copyDisplayUrl}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy Display URL'}
                </Button>
                <Link to={`/cloakroom/display/${session.session_code}`} target="_blank">
                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                  >
                    Open Display in New Tab
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Prize History */}
            {session.prize_rounds?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">Winners</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {[...session.prize_rounds].reverse().map((round, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-2 rounded bg-slate-700/50"
                      >
                        <span className="text-amber-400 font-medium">{round.prize_label}</span>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: getColorHex(round.winning_ticket.color) }}
                          />
                          <span className="text-white font-mono">
                            {round.winning_ticket.serial} - #{round.winning_ticket.number}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
